import { z } from 'zod'
import { passwordSchema } from '../validators/password.validator.js'

/** Login request body. Shared so the web form and the API validate identically. */
export const loginInputSchema = z
  .object({
    email: z.string().trim().toLowerCase().email(),
    password: z.string().min(1, 'Password is required'),
  })
  .strict()

export type LoginInput = z.infer<typeof loginInputSchema>

/**
 * Changing your own password.
 *
 * The current password is checked for presence only, NOT against the password
 * policy. Accounts predate the policy — the first user of an install is created
 * by the bootstrap script, which can set something shorter than the twelve
 * characters the invite form demands — and validating the old value would lock
 * exactly those people out of the screen that exists to fix it.
 *
 * The new password gets the full policy, so every password that leaves this
 * screen meets it even when the one going in did not.
 */
export const changePasswordInputSchema = z
  .object({
    currentPassword: z.string().min(1, 'Enter your current password'),
    newPassword: passwordSchema,
  })
  .strict()
  .refine((input) => input.currentPassword !== input.newPassword, {
    message: 'The new password must be different from the current one',
    path: ['newPassword'],
  })

export type ChangePasswordInput = z.infer<typeof changePasswordInputSchema>
